import { useTranslation } from 'react-i18next';

import { useCompareView } from '~/components/common/enrichment-compare-grid/compare-view-mode-context';
import { CompareViewToggle } from '~/components/common/enrichment-compare-grid/compare-view-toggle';

import { useViewBefore } from './view-before-context';
import { ViewBeforeToggle } from './view-before-toggle';

/** One dot + label legend pill. */
function LegendItem({
  colorClassName,
  label,
  testId,
}: {
  colorClassName: string;
  label: string;
  testId: string;
}) {
  return (
    <span className="text-muted-foreground flex items-center gap-1.5 text-xs" data-testid={testId}>
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${colorClassName}`} />
      {label}
    </span>
  );
}

/**
 * Explains the Enriched/Modified cell background colors used by `DiffCell`, and
 * hosts the preview section's inline controls when their providers are present:
 * the Table/Compare view switch (`CompareViewCtx`) and the "Show Original
 * Content" toggle (`ViewBeforeCtx`).
 *
 * The before-toggle is hidden in compare mode — that grid always shows the feed
 * value alongside the enriched one, so the switch would be a no-op.
 */
export function DiffLegend({
  testId = 'diff-legend',
}: {
  /** Automation hook: root id; legend items, the before-toggle and the view toggle derive from it. */
  testId?: string;
}) {
  const { t } = useTranslation('content-enrichment');
  const viewBefore = useViewBefore();
  const compareView = useCompareView();
  const isCompareMode = compareView?.mode === 'compare';

  return (
    <div className="flex shrink-0 items-center gap-6" data-testid={testId}>
      <div className="flex items-center gap-4">
        <LegendItem
          colorClassName="bg-success"
          label={t('diffTable.legend.enriched')}
          testId={`${testId}-item-enriched`}
        />
        <LegendItem
          colorClassName="bg-warning"
          label={t('diffTable.legend.modified')}
          testId={`${testId}-item-modified`}
        />
      </div>
      {viewBefore && !isCompareMode && (
        <ViewBeforeToggle {...viewBefore} testId={`${testId}-view-before-toggle`} />
      )}
      {compareView && <CompareViewToggle {...compareView} testId={`${testId}-view-tabs`} />}
    </div>
  );
}
